'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { Star, CheckCircle2 } from 'lucide-react';

interface MosaicColumn {
  id: number;
  heightClass: string;
  visibilityClass: string;
  gapClass: string;
  images: {
    src: string;
    alt: string;
  }[];
}

const MOSAIC_COLUMNS: MosaicColumn[] = [
  // Col 1: 2 stacked portrait tiles (outer left - visible on tablet and desktop)
  {
    id: 1,
    heightClass: 'h-[120px] sm:h-[190px] md:h-[250px] lg:h-[300px] xl:h-[320px]',
    visibilityClass: 'hidden sm:flex',
    gapClass: 'gap-1.5 sm:gap-2 lg:gap-2.5',
    images: [
      { src: '/images/mosaic_snack_cozy.jpg', alt: 'Cozy living room snacking moment' },
      { src: '/images/pouch_banana_chips.png', alt: 'Fresh golden Kerala banana chips pouch' },
    ],
  },
  // Col 2: 3 stacked landscape tiles (inner left)
  {
    id: 2,
    heightClass: 'h-[145px] sm:h-[225px] md:h-[295px] lg:h-[350px] xl:h-[375px]',
    visibilityClass: 'flex',
    gapClass: 'gap-1 sm:gap-1.5 lg:gap-2',
    images: [
      { src: '/images/pouch_potato_chips.png', alt: 'Crispy spiced potato chips pouch' },
      { src: '/images/pouch_ribbon_pakoda.png', alt: 'Crispy golden Ribbon Pakoda pouch' },
      { src: '/images/pouch_sharkara_upperi.png', alt: 'Sweet Upperi jaggery banana chips pouch' },
    ],
  },
  // Col 3: 1 tall vertical portrait (mid left)
  {
    id: 3,
    heightClass: 'h-[175px] sm:h-[265px] md:h-[345px] lg:h-[410px] xl:h-[435px]',
    visibilityClass: 'flex',
    gapClass: '',
    images: [
      { src: '/images/pouch_jackfruit_chips.png', alt: 'Crispy Jackfruit Chips pouch' },
    ],
  },
  // Col 4: 1 tall vertical portrait - peak hero (center left)
  {
    id: 4,
    heightClass: 'h-[210px] sm:h-[305px] md:h-[395px] lg:h-[465px] xl:h-[490px]',
    visibilityClass: 'flex',
    gapClass: '',
    images: [
      { src: '/images/mosaic_brand_pouches.jpg', alt: 'Elavarasi signature artisan snack pouches' },
    ],
  },
  // Col 5: 1 tall vertical portrait - peak hero (center right)
  {
    id: 5,
    heightClass: 'h-[210px] sm:h-[305px] md:h-[395px] lg:h-[465px] xl:h-[490px]',
    visibilityClass: 'flex',
    gapClass: '',
    images: [
      { src: '/images/mosaic_friends_laughing.jpg', alt: 'Friends happily sharing Elavarasi snacks' },
    ],
  },
  // Col 6: 1 tall vertical portrait (mid right)
  {
    id: 6,
    heightClass: 'h-[175px] sm:h-[265px] md:h-[345px] lg:h-[410px] xl:h-[435px]',
    visibilityClass: 'flex',
    gapClass: '',
    images: [
      { src: '/images/pouch_ghee_mysore_pak.png', alt: 'Elavarasi Ghee Mysore Pak pouch' },
    ],
  },
  // Col 7: 3 stacked landscape tiles (inner right)
  {
    id: 7,
    heightClass: 'h-[145px] sm:h-[225px] md:h-[295px] lg:h-[350px] xl:h-[375px]',
    visibilityClass: 'flex',
    gapClass: 'gap-1 sm:gap-1.5 lg:gap-2',
    images: [
      { src: '/images/pouch_thattai_crackers.png', alt: 'Chettinad Thattai crisps pouch' },
      { src: '/images/pouch_tapioca_chips.png', alt: 'Crispy wafer-thin Tapioca chips pouch' },
      { src: '/images/pouch_madras_mixture.png', alt: 'Madras Royal mixture pouch' },
    ],
  },
  // Col 8: 2 stacked portrait tiles (outer right - visible on tablet and desktop)
  {
    id: 8,
    heightClass: 'h-[120px] sm:h-[190px] md:h-[250px] lg:h-[300px] xl:h-[320px]',
    visibilityClass: 'hidden sm:flex',
    gapClass: 'gap-1.5 sm:gap-2 lg:gap-2.5',
    images: [
      { src: '/images/mosaic_hiking_portrait.jpg', alt: 'Outdoor adventure snack break' },
      { src: '/images/pouch_kai_murukku.png', alt: 'Handcrafted golden Kai Murukku swirls pouch' },
    ],
  },
];

const TESTIMONIALS = [
  {
    id: 1,
    quote:
      "The Kai Murukku tastes just like how our paati made it in Madurai. Perfectly brittle, fresh butter aroma, and zero chemical aftertaste. A staple in our family tea times now!",
    name: 'Dr. Meenakshi Sundaram',
    role: 'Heritage Food Enthusiast • Chennai',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=120&auto=format&fit=crop&q=80',
  },
  {
    id: 2,
    quote:
      "Ordered 80 festive gift boxes for our corporate annual gala in Bengaluru. The custom logo sleeve was impeccably printed, and every box arrived crisp and unbroken. Superb service!",
    name: 'Anand Ramakrishnan',
    role: 'Operations VP, Tech Services • Bengaluru',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80',
  },
  {
    id: 3,
    quote:
      "The Nendran banana chips fried in cold-pressed coconut oil are in a league of their own. Wafer-thin, delicate, and addictive. Even our kids can’t stop munching them.",
    name: 'Lavanya Venkatesh',
    role: 'Home Architect • Coimbatore',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80',
  },
];

export default function TrustAndNewsletterSection() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setSubscribed(true);
    setTimeout(() => {
      setSubscribed(false);
      setEmail('');
    }, 4000);
  };

  return (
    <section id="reviews" className="py-12 sm:py-16 lg:py-20 bg-white scroll-mt-20 overflow-hidden select-none w-full">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        
        {/* 1. Curved Mosaic Arch Gallery - Fully Responsive, Zero Horizontal Shift or Scroll */}
        <div className="w-full overflow-hidden flex items-center justify-center py-2">
          <div className="w-full flex items-center justify-center gap-1.5 sm:gap-2.5 md:gap-3 lg:gap-3.5 max-w-full">
            {MOSAIC_COLUMNS.map((col) => (
              <div
                key={col.id}
                className={`${col.visibilityClass} ${col.heightClass} flex-1 min-w-0 max-w-[54px] sm:max-w-[80px] md:max-w-[110px] lg:max-w-[136px] xl:max-w-[144px] flex-col ${col.gapClass}`}
              >
                {col.images.map((img, imgIdx) => (
                  <div
                    key={imgIdx}
                    className="relative flex-1 w-full rounded-lg sm:rounded-2xl lg:rounded-3xl overflow-hidden bg-neutral-100 shadow-2xs hover:shadow-md hover:scale-[1.02] transition-all duration-300 group cursor-pointer border border-neutral-100/80"
                  >
                    <Image
                      src={img.src}
                      alt={img.alt}
                      fill
                      sizes="(max-width: 640px) 54px, (max-width: 768px) 80px, (max-width: 1024px) 110px, 144px"
                      unoptimized
                      className="object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/4 group-hover:bg-transparent transition-colors" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* 2. Centered Section Header */}
        <div className="text-center max-w-2xl mx-auto mt-10 sm:mt-14 mb-8 sm:mb-12 px-4">
          <div className="inline-flex items-center justify-center px-3.5 py-1 rounded-full border border-neutral-300/80 bg-[#FAF7F2] text-xs font-semibold text-neutral-700 shadow-2xs mb-3">
            Customer Testimonials
          </div>
          <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-[#0F382A] tracking-tight leading-[1.18]">
            Trusted by Families &amp; Food Lovers <br className="hidden sm:inline" />
            <span className="text-neutral-400 font-normal">Across India &amp; Overseas</span>
          </h2>
        </div>

        {/* 3. Three-Column Reviews Grid - Structured for clean mobile cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8 max-w-6xl mx-auto">
          {TESTIMONIALS.map((t) => (
            <div
              key={t.id}
              className="flex flex-col justify-between space-y-4 bg-[#FCFBF8] border border-neutral-200/70 p-5 sm:p-6 rounded-2xl sm:rounded-3xl shadow-xs hover:shadow-md transition-shadow"
            >
              {/* 5 Stars */}
              <div className="flex items-center space-x-1 text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 stroke-amber-400" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-xs sm:text-[13px] text-neutral-600 font-normal leading-relaxed italic">
                &ldquo;{t.quote}&rdquo;
              </p>

              {/* Author Row */}
              <div className="flex items-center space-x-3 pt-2 border-t border-neutral-200/50">
                <div className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden shrink-0 ring-2 ring-neutral-200">
                  <Image
                    src={t.avatar}
                    alt={t.name}
                    fill
                    sizes="40px"
                    unoptimized
                    className="object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-bold text-xs sm:text-sm text-neutral-900 leading-tight">
                    {t.name}
                  </h4>
                  <p className="text-[10px] sm:text-[11px] text-neutral-400 font-medium mt-0.5">
                    {t.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 4. Newsletter & VIP Community Footnote Bar */}
        <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-neutral-200/60 max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <p className="font-bold text-xs sm:text-sm text-neutral-900">
              Join 50,000+ Happy Food Lovers
            </p>
            <p className="text-[11px] text-neutral-500 mt-0.5">
              Subscribe for secret weekend discount codes &amp; fresh batch alert drops.
            </p>
          </div>

          {subscribed ? (
            <div className="flex items-center space-x-2 text-emerald-700 bg-emerald-50 px-4 py-2 rounded-full border border-emerald-200 text-xs font-bold">
              <CheckCircle2 className="w-4 h-4 stroke-[2.5]" />
              <span>You&apos;re on the VIP list! Welcome!</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex items-center space-x-2 w-full sm:w-auto">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="bg-neutral-50 border border-neutral-200 rounded-full px-4 py-2 text-xs text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition-colors w-full sm:w-60"
              />
              <button
                type="submit"
                className="px-5 py-2 rounded-full bg-neutral-900 hover:bg-neutral-800 text-white text-xs font-bold uppercase tracking-wider transition-colors shrink-0 cursor-pointer shadow-xs active:scale-95"
              >
                Join
              </button>
            </form>
          )}
        </div>

      </div>
    </section>
  );
}
